import React, { useEffect, useState } from "react";
import { Play, Video } from "lucide-react";

/**
 * <TopicVideoRail /> — kid-safe video shelf, fetched from the backend
 * `topic_videos` collection.
 *
 * Displays every video attached to (topic + tier). Each thumbnail is
 * click-to-play — no video is loaded until the child taps it. YouTube
 * videos embed via youtube-nocookie with a tight parameter set.
 *
 * Renders nothing when the API returns no videos, so LessonReader can
 * safely mount it unconditionally alongside the static <LessonVideo/>.
 */
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LLM-verified relevance filter — Feb 2026.
 * Queries `/api/videos/relevance-check` for each candidate video (cached
 * both client-side and server-side). Any video the reviewer flags as
 * off-topic, off-outline, or off-age is removed from the shelf so the
 * child only sees clips that clearly match their lesson.
 */
function _cacheKey(code, videoId, tier) {
  return `mwq-lv-verdict::${code}::${videoId}::${tier || ""}`;
}
function _readCache(code, videoId, tier) {
  try { return localStorage.getItem(_cacheKey(code, videoId, tier)) || null; }
  catch { return null; }
}
function _writeCache(code, videoId, tier, verdict) {
  try { localStorage.setItem(_cacheKey(code, videoId, tier), verdict); }
  catch { /* quota-full → silent */ }
}
async function filterRelevant(list, lessonCode, tier) {
  const results = await Promise.all(list.map(async (v) => {
    const cached = _readCache(lessonCode, v.video_id, tier);
    if (cached === "yes") return v;
    if (cached === "no")  return null;
    try {
      const r = await fetch(`${API}/api/videos/relevance-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lesson_code: lessonCode,
          video_id: v.video_id,
          video_title: v.title || "",
          video_channel: v.channel || v.channel_name || "",
          video_description: v.description || "",
          tier: tier || "",
        }),
      });
      if (!r.ok) return v; // network fail → keep the video (fail-open)
      const j = await r.json();
      const verdict = j.relevant === true ? "yes" : "no";
      _writeCache(lessonCode, v.video_id, tier, verdict);
      return verdict === "yes" ? v : null;
    } catch { return v; }
  }));
  return results.filter(Boolean);
}

function embedUrlFor(provider, videoId, autoplay = false) {
  if (provider === "youtube") {
    const params = [
      "rel=0", "modestbranding=1", "controls=1", "playsinline=1",
      "iv_load_policy=3", "fs=1",
    ];
    if (autoplay) params.push("autoplay=1");
    return `https://www.youtube-nocookie.com/embed/${videoId}?${params.join("&")}`;
  }
  if (provider === "vimeo") {
    return `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0${autoplay ? "&autoplay=1" : ""}`;
  }
  return null; // mp4 handled directly by <video>
}

export default function TopicVideoRail({ topic, tier, subject, lessonCode }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null); // video.id being played

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!tier || (!topic && !lessonCode)) { setLoading(false); return; }
      try {
        const q = new URLSearchParams({ tier });
        // Prefer lesson_code (server resolves via lesson_topic_links).
        // Falls back to topic string when no code is available.
        if (lessonCode) q.set("lesson_code", lessonCode);
        else if (topic) q.set("topic_id", topic);
        const r = await fetch(`${API}/api/topic-videos?${q.toString()}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        let list = data.videos || [];
        // LLM relevance filter — hide any candidate the reviewer marked
        // as off-topic or not age-appropriate. Fires in parallel.
        if (lessonCode && list.length) {
          list = await filterRelevant(list, lessonCode, tier);
        }
        if (!cancelled) setVideos(list);
      } catch {
        if (!cancelled) setVideos([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [topic, tier, lessonCode]);

  if (loading || videos.length === 0) return null;

  return (
    <div
      className="mt-6 rounded-3xl border-4 border-slate-900 bg-white p-3 sm:p-4 shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
      data-testid="topic-video-rail"
    >
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">Watch more</p>
          <h3 className="font-heading text-lg font-bold text-slate-900 leading-tight">
            Videos about <span className="text-emerald-700">{topic}</span>
          </h3>
        </div>
        <span className="shrink-0 text-[10px] font-bold text-slate-500">
          {videos.length} clip{videos.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {videos.map((v) => (
          <VideoCard
            key={v.id}
            v={v}
            isPlaying={playing === v.id}
            onPlay={() => setPlaying(v.id)}
          />
        ))}
      </div>
      {subject && (
        <p className="mt-3 text-[10px] font-bold text-slate-500">
          Kid-safe · {subject} · {tier}
        </p>
      )}
    </div>
  );
}

function VideoCard({ v, isPlaying, onPlay }) {
  const embed = embedUrlFor(v.provider, v.video_id, true);
  const thumb = v.thumbnail || (v.provider === "youtube" ? `https://img.youtube.com/vi/${v.video_id}/hqdefault.jpg` : "");

  return (
    <div
      className="rounded-2xl border-[3px] border-slate-900 bg-slate-900 overflow-hidden shadow-[4px_4px_0_0_rgba(15,23,42,1)]"
      data-testid={`topic-video-card-${v.id}`}
    >
      <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
        {isPlaying && embed ? (
          <iframe
            title={v.title}
            src={embed}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; encrypted-media; picture-in-picture"
            referrerPolicy="strict-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            data-testid={`topic-video-iframe-${v.id}`}
          />
        ) : isPlaying && v.provider === "mp4" ? (
          <video
            src={v.url}
            controls
            autoPlay
            className="absolute inset-0 w-full h-full"
            data-testid={`topic-video-mp4-${v.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={onPlay}
            data-testid={`topic-video-play-${v.id}`}
            aria-label={`Play ${v.title}`}
            className="absolute inset-0 flex items-center justify-center group focus:outline-none focus-visible:ring-4 focus-visible:ring-emerald-400"
            style={thumb ? { backgroundImage: `url(${thumb})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}
          >
            <span className="absolute inset-0 bg-black/50 group-hover:bg-black/40 transition-colors" aria-hidden />
            <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-400 border-4 border-white shadow-[0_4px_0_0_rgba(15,23,42,0.6)] transition-transform group-hover:scale-105">
              <Play size={22} strokeWidth={3} className="text-slate-900 ml-1" aria-hidden />
            </span>
          </button>
        )}
      </div>
      <div className="px-3 py-2 bg-slate-800 text-slate-100 flex items-start gap-2">
        <Video size={12} strokeWidth={2.5} className="mt-1 shrink-0 text-emerald-300" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs font-semibold leading-tight line-clamp-2">{v.title}</p>
          {(v.channel || v.duration) && (
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">
              {v.channel}{v.channel && v.duration ? " · " : ""}{v.duration}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
